import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { SideOverlay } from "../../../../components";
import { DeleteModal } from "../../../../components";
import NotebookHeader from "./NotebookHeader";
import AddContextModal from "./AddContextModal";
import AddSourceOverlayContent from "./AddSourceOverlayContent";
import SourcesTree from "./SourcesTree";
import EsSampleKeysSync from "./EsSampleKeysSync";
import type { SourceNode } from "./sources-types";
import { useStore } from "../../../../helpers";
import { useSourceList, useDeleteSource } from "../../tars-hooks";
import type { SourceListItem } from "../../tars-hooks/useSourceList";

const SIDEBAR_MIN_WIDTH = 240;

const NotebookSidebarContent = observer(function NotebookSidebarContent({
  notebookId,
}: {
  notebookId: number | null;
}) {
  const { tarsStore, tarsAnalyticsStore } = useStore();
  const { mutateAsync: deleteSource } = useDeleteSource();
  const [isContextOpen, setIsContextOpen] = useState(false);
  const [selectedNotebookId, setSelectedNotebookId] = useState<number | null>(null);
  const [isAddSourceOpen, setIsAddSourceOpen] = useState(false);
  const [addSourceParentId, setAddSourceParentId] = useState<string | null>(null);
  const [addSourceParentLabel, setAddSourceParentLabel] = useState("Root");
  const [editSourceNode, setEditSourceNode] = useState<SourceNode | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ id: string; label: string } | null>(null);

  const listParams =
    notebookId != null && !Number.isNaN(notebookId)
      ? { notebook_id: notebookId, parent_id: null, offset: 0, limit: 100 }
      : null;
  const { data: listData } = useSourceList(listParams);

  useEffect(() => {
    const raw = listData?.sources ?? (Array.isArray(listData) ? listData : null);
    if (raw && Array.isArray(raw)) {
      tarsStore.setSourcesFromList(raw as SourceListItem[]);
    } else if (!notebookId) {
      tarsStore.setSourcesFromList([]);
    }
  }, [listData, notebookId, tarsStore]);

  const getNode = tarsStore.getNode.bind(tarsStore);

  const handleOpenContext = (notebookId: number) => {
    setSelectedNotebookId(notebookId);
    setIsContextOpen(true);
  };

  const handleCloseContext = () => setIsContextOpen(false);

  const handleOpenAddSource = (parentId: string | null, parentLabel: string) => {
    setEditSourceNode(null);
    setAddSourceParentId(parentId);
    setAddSourceParentLabel(parentLabel);
    setIsAddSourceOpen(true);
  };

  const handleOpenEditSource = (node: SourceNode) => {
    setEditSourceNode(node);
    setAddSourceParentId(node.parentId);
    setAddSourceParentLabel(getNode(node.parentId ?? "")?.name ?? "Root");
    setIsAddSourceOpen(true);
  };

  const handleCloseAddSource = () => {
    setIsAddSourceOpen(false);
    setEditSourceNode(null);
  };

  const handleDelete = (id: string, label: string) => {
    setDeleteModal({ id, label });
  };

  const handleDeleteConfirm = async (_e: unknown, id: string) => {
    const sourceIdNum = Number(id);
    if (Number.isNaN(sourceIdNum)) {
      tarsStore.remove(id);
      setDeleteModal(null);
      return;
    }
    try {
      await deleteSource({
        source_id: sourceIdNum,
        ...(notebookId != null && !Number.isNaN(notebookId) ? { notebook_id: notebookId } : {}),
      });
      tarsStore.remove(id);
      setDeleteModal(null);
    } catch {
      // Error handled by useDeleteSource
    }
  };

  return (
    <>
      <aside
        className="flex h-full shrink-0 flex-col border-r border-gray-200 prototype--documentationContainer"
        style={{
          width: tarsAnalyticsStore.sidebarWidth,
          minWidth: SIDEBAR_MIN_WIDTH,
        }}
      >
        <div className="flex flex-1 min-h-0 flex-col prototype--background bg-[#F9FAFB]">
          <div className="flex flex-1 min-h-0 flex-col prototype--content overflow-hidden">
            <NotebookHeader onAddContext={handleOpenContext} onAddSource={handleOpenAddSource} />
            <div className="flex-1 min-h-0 flex flex-col prototype--contentMiddle overflow-hidden">
              <EsSampleKeysSync />
              <SourcesTree
                notebookId={notebookId}
                onAddSource={handleOpenAddSource}
                onEditSource={handleOpenEditSource}
                onDelete={handleDelete}
              />
            </div>
          </div>
        </div>
      </aside>

      <AddContextModal
        open={isContextOpen}
        onClose={handleCloseContext}
        notebookId={selectedNotebookId}
      />

      <SideOverlay
        show={isAddSourceOpen}
        onClose={handleCloseAddSource}
        title={editSourceNode ? "Edit source" : "Add source"}
        size="large"
      >
        <AddSourceOverlayContent
          parentId={addSourceParentId}
          parentLabel={addSourceParentLabel}
          editNode={editSourceNode}
          onClose={handleCloseAddSource}
        />
      </SideOverlay>

      <DeleteModal
        isOpen={!!deleteModal}
        id={deleteModal?.id}
        desc={deleteModal ? `Delete "${deleteModal.label}"?${deleteModal.id.startsWith("folder") ? " Contents will be removed." : ""}` : ""}
        onDeleteClick={handleDeleteConfirm}
        onCancelClick={() => setDeleteModal(null)}
      />
    </>
  );
});

const NotebookSidebar = () => {
  const { notebookId } = useParams<{ notebookId: string }>();
  const nid = notebookId != null ? Number(notebookId) : null;
  const notebookIdNum = nid != null && !Number.isNaN(nid) ? nid : null;
  return <NotebookSidebarContent notebookId={notebookIdNum} />;
};

export default NotebookSidebar;
